<template>
  <div class="app">
    <!-- 导航栏 -->
    <Navbar />

    <!-- 主内容区 -->
    <main class="main-content">
      <!-- 首页 -->
      <Home v-if="currentPage === 'home'" />

      <!-- Leaderboard -->
      <Leaderboard v-else-if="currentPage === 'leaderboard'" />

      <!-- About -->
      <About v-else-if="currentPage === 'about'" />

      <!-- How to Play -->
      <HowToPlay v-else-if="currentPage === 'how-to-play'" />

      <!-- Tips -->
      <Tips v-else-if="currentPage === 'tips'" />

      <!-- FAQ -->
      <FAQ v-else-if="currentPage === 'faq'" />

      <!-- Support -->
      <Support v-else-if="currentPage === 'support'" />

      <!-- Contact -->
      <Contact v-else-if="currentPage === 'contact'" />

      <!-- Privacy -->
      <Privacy v-else-if="currentPage === 'privacy'" />

      <!-- Terms -->
      <Terms v-else-if="currentPage === 'terms'" />

      <!-- Strategies -->
      <Strategies v-else-if="currentPage === 'strategies'" />
      <StrategyDetail v-else-if="currentPage === 'strategy-detail'" />

      <!-- 游戏页面 -->
      <div v-else-if="isGameRoute" class="game-container">
        <!-- Word Poker模式选择 -->
        <GameModeSelector v-if="showModeSelector" />

        <!-- 扑克游戏 -->
        <GameBoard v-else-if="currentGameType === 'poker'" />

        <!-- 数独游戏 -->
        <SudokuGame v-else-if="currentGameType === 'sudoku'" />

        <!-- 脑筋急转弯游戏 -->
        <RiddlesGame v-else-if="currentGameType === 'riddles'" />
      </div>
    </main>


    <!-- 页脚 -->
    <Footer />
    
    <!-- Cookie Consent Banner -->
    <CookieConsent />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

// Components
import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'
import CookieConsent from '@/components/CookieConsent.vue'

// Pages
import Home from '@/pages/Home.vue'
import Leaderboard from '@/pages/Leaderboard.vue'
import About from '@/pages/About.vue'
import HowToPlay from '@/pages/HowToPlay.vue'
import Tips from '@/pages/Tips.vue'
import FAQ from '@/pages/FAQ.vue'
import Support from '@/pages/Support.vue'
import Contact from '@/pages/Contact.vue'
import Privacy from '@/pages/Privacy.vue'
import Terms from '@/pages/Terms.vue'
import Strategies from '@/pages/Strategies.vue'
import StrategyDetail from '@/pages/StrategyDetail.vue'

// Games
import GameBoard from '@/components/GameBoard.vue'
import GameModeSelector from '@/components/GameModeSelector.vue'
import SudokuGame from '@/games/SudokuGame.vue'
import RiddlesGame from '@/games/RiddlesGame.vue'

// Stores & Utils
import { useGameStore } from '@/stores/gameStore'
import { t } from '@/utils/i18n'
import { initAnalytics } from '@/utils/analytics'

const currentPath = ref((window.location.hash || '#/').slice(1))

const gameStore = useGameStore()
const currentGameType = ref('poker')

// 确定当前应该显示哪个页面
const currentPage = computed(() => {
  const path = currentPath.value

  if (path.includes('/leaderboard')) return 'leaderboard'
  if (path.includes('/about')) return 'about'
  if (path.includes('/how-to-play')) return 'how-to-play'
  if (path.includes('/tips')) return 'tips'
  if (path.includes('/faq')) return 'faq'
  if (path.includes('/support')) return 'support'
  if (path.includes('/contact')) return 'contact'
  if (path.includes('/privacy')) return 'privacy'
  if (path.includes('/terms')) return 'terms'
  
  // Strategy Routes
  if (path.includes('/strategies/')) return 'strategy-detail'
  if (path.includes('/strategies')) return 'strategies'

  if (path.includes('/game') || path.includes('/games/')) return 'game'

  return 'home'
})

// 检查当前是否在游戏路由
const isGameRoute = computed(() => {
  return currentPage.value === 'game'
})

// 检查是否显示Word Poker模式选择界面
const showModeSelector = computed(() => {
  const path = currentPath.value
  // 只有当路径恰好是 /games/poker 或 /games/poker/ 时才显示模式选择器
  // 如果路径包含具体的模式（classic/lightning/coop），则不显示
  return path === '/games/poker' || path === '/games/poker/'
})

// 从路径提取游戏类型
const extractGameType = (path) => {
  if (path.includes('/games/sudoku')) return 'sudoku'
  if (path.includes('/games/riddles')) return 'riddles'
  if (path.includes('/game') || path.includes('/games/poker')) return 'poker'
  return 'poker'
}

// 监听路由变化
const handleHashChange = () => {
  currentPath.value = (window.location.hash || '#/').slice(1)
  currentGameType.value = extractGameType(currentPath.value)

  // 如果导航到扑克游戏具体模式，初始化游戏
  if (currentGameType.value === 'poker' && currentPath.value.includes('/games/poker/')) {
    // 从路径提取模式：/games/poker/classic -> classic
    const modeMatch = currentPath.value.match(/\/games\/poker\/(\w+)/)
    if (modeMatch) {
      const mode = modeMatch[1]
      if (['classic', 'lightning', 'coop'].includes(mode)) {
        gameStore.gameMode = mode
        gameStore.startGame()
      }
    }
  }
}

onMounted(() => {
  // 初始化游戏类型
  currentGameType.value = extractGameType(currentPath.value)

  window.addEventListener('hashchange', handleHashChange)
  initAnalytics() // Initialize analytics if consent was previously given
})

onBeforeUnmount(() => {
  window.removeEventListener('hashchange', handleHashChange)
})
</script>

<style scoped>
.app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--color-bg-light);
}

.main-content {
  flex: 1;
  width: 100%;
}

.game-container {
  width: 100%;
  min-height: calc(100vh - 64px); /* 减去导航栏高度 */
}
</style>
